import { Link } from 'react-router-dom';
import { useLearnFrom, useMonitoring } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { countLine, flagTally, type MonitoringSignal } from '@/lib/monitoring-data';
import { NOT_MEASURED, plural, when } from '@/lib/format';
import s from './console.module.css';

/**
 * What we monitor (pv-auth GET /provider/admin/monitoring): every signal
 * PostureVision saves, one block each, generated on the server from the one
 * checked-in catalog (src/services/admin/monitoringCatalog.ts), so this page
 * and command-center MONITORING_CATALOG.md cannot drift apart. Each flag is
 * its live value on the server, true or false; each table carries the rows
 * written yesterday (UTC) and the total. Below it, the members we hold the
 * most about, to open as real examples.
 */

/** The counts' day is a UTC day, so it is named in UTC, not in the viewer's zone. */
const utcDay = (iso: string): string => new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'UTC' });

function SignalBlock({ sig }: { sig: MonitoringSignal }) {
  return (
    <div className={s.card} data-signal={sig.key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <strong>{sig.name}</strong>
        <span className={s.pill}>{sig.sourceLabel}</span>
      </div>
      <dl className={s.facts}>
        <dt>What is saved</dt>
        <dd>
          {sig.tables.map((t) => (
            <div key={`${t.model}-${t.whereLabel ?? ''}`} data-table={t.model} style={{ marginBottom: 4 }}>
              <span className={s.flagKey}>{t.model}</span>{t.whereLabel ? <span className={s.sub}> ({t.whereLabel})</span> : null}: {t.fields.join(', ')}
              <div className={t.built && t.total !== null ? s.sub : `${s.sub} ${s.missing}`} data-count>{countLine(t)}</div>
            </div>
          ))}
        </dd>
        <dt>How often</dt><dd>{sig.cadence}</dd>
        <dt>Kept</dt><dd>{sig.retention}</dd>
        <dt>Read by</dt><dd>{sig.readerLabels.length ? sig.readerLabels.join(', ') : 'nothing yet'}{sig.readersNote ? <span className={s.sub}> · {sig.readersNote}</span> : null}</dd>
        <dt>Flag</dt>
        <dd>
          {sig.flags.length === 0 ? 'none, always on' : sig.flags.map((f) => (
            <div key={f.name} className={s.flagRow} data-flag={f.name} style={{ padding: '2px 0', border: 'none' }}>
              <span className={`${s.pill} ${f.live ? s.pill_on : s.pill_off}`}>{f.live ? 'on' : 'off'}</span>
              <span className={s.flagKey}>{f.name}</span>
              <span className={s.flagWhat}>{f.gates}{f.live === f.defaultOn ? '' : ` (default ${f.defaultOn ? 'on' : 'off'})`}{f.expectedIn ? ` · added by ${f.expectedIn}` : ''}</span>
            </div>
          ))}
        </dd>
        <dt>Privacy</dt><dd>{sig.privacy}</dd>
      </dl>
    </div>
  );
}

function LearnFrom() {
  const q = useLearnFrom(50);
  const d = q.data;
  return (
    <Section title="Members to learn from" summary={d ? `${d.ranking} QA accounts left out.` : 'members ranked by how much we hold about them'} count={d ? d.rows.length : null} defaultOpen id="learn-from">
      {q.isError && <div className={s.error}>Could not load the list. This needs GET /provider/admin/understanding/members.</div>}
      {d && d.unavailable.length > 0 && <div className={s.jWarn}>Not read this time, counted as none for everyone: {d.unavailable.join(', ')}.</div>}
      {d && d.rows.length === 0 && <div className={s.empty}>Nobody has a check, a session, an activation, a told day or a finding yet.</div>}
      {d && d.rows.length > 0 && (
        <div className={s.tableWrap} data-table="learn-from">
          <table className={s.table}>
            <thead><tr><th>Person</th><th>Checks</th><th>Active Mode</th><th>Activations</th><th>Told days</th><th>Findings</th><th>Kinds held</th><th /></tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.userId} data-person={r.userId}>
                  <td>{r.name || r.email}<div className={s.sub}>{r.name ? r.email : ''}</div></td>
                  <td>{r.checks.toLocaleString()}</td>
                  <td>{r.activeSessions.toLocaleString()}</td>
                  <td>{r.activations.toLocaleString()}</td>
                  <td>{r.toldDays.toLocaleString()}</td>
                  <td>{r.findings.toLocaleString()}</td>
                  <td>{r.breadth} of 5</td>
                  <td><Link className={s.linkA} to={`/people/${encodeURIComponent(r.userId)}#understanding`}>Open their Understanding</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d && <div className={s.sub} style={{ marginTop: 6 }}>{d.considered.toLocaleString()} people hold at least one of the five · read {when(d.generatedAt)}</div>}
    </Section>
  );
}

export default function MonitoringPage() {
  const q = useMonitoring();
  const d = q.data;
  const tally = d ? flagTally(d.signals) : null;
  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <div>
          <h1 className={s.pageTitle}>What we monitor</h1>
          <span className={s.pageSub}>
            every signal we save, what reads it, the flag that gates it and its live value
            {d ? ` · yesterday is ${utcDay(d.yesterday.from)} (UTC) · ${plural(tally!.on, 'flag')} on, ${tally!.off} off` : ''}
          </span>
        </div>
      </div>

      {q.isError && <div className={s.error}>GET /provider/admin/monitoring did not load. The counts are {NOT_MEASURED} right now.</div>}
      {q.isLoading && <div className={s.sub}>Loading the catalog.</div>}

      {d && (
        <>
          <div className={s.notice} data-rules>
            <strong>Rules every row shares</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{d.rules.map((r) => <li key={r}>{r}</li>)}</ul>
          </div>
          <div className={s.list} data-signals>{d.signals.map((sig) => <SignalBlock key={sig.key} sig={sig} />)}</div>
          <div className={s.sub}>Generated from pv-auth src/services/admin/monitoringCatalog.ts, the same source as command-center MONITORING_CATALOG.md · read {when(d.generatedAt)}</div>
        </>
      )}

      <LearnFrom />
    </div>
  );
}
